import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, PaperPlaneTilt, WhatsappLogo } from "@phosphor-icons/react";
import { API } from "@/lib/api";
import { trackLeaseIntentClick } from "@/lib/gtag";

const WELCOME = "Olá! 👋 Sou o Carlos, do VM Shopping. Posso tirar dúvidas rápidas e te conectar ao nosso atendimento executivo no WhatsApp para alugar um espaço. Como posso ajudar?";
const SUGGESTIONS = ["Quero alugar um espaço", "Quais os horários?", "Onde fica o shopping?"];

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", content: WELCOME }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sessionId = useRef(`web-${Math.random().toString(36).slice(2)}-${Date.now()}`);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);

    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: msg }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + chunk };
          return copy;
        });
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Desculpe, tive um problema. Tente novamente." };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.95 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-4 sm:right-6 z-50 w-[calc(100vw-2rem)] sm:w-[380px] h-[520px] max-h-[75vh] rounded-2xl overflow-hidden border border-vm-gold/25 bg-vm-surface/95 backdrop-blur-xl flex flex-col shadow-2xl"
            data-testid="chat-panel"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-vm-border bg-vm-muted/50">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-full overflow-hidden bg-gradient-to-br from-vm-gold to-vm-goldSoft flex items-center justify-center ring-1 ring-vm-gold/40">
                  <img src="/chat-avatar.png" alt="Atendente" className="h-full w-full object-cover" style={{ objectPosition: "52% 6%", transform: "scale(1.35)" }} />
                </div>
                <div>
                  <p className="text-sm font-medium leading-tight">Fale Conosco</p>
                  <p className="text-[11px] text-vm-gold">VM Piabetá · online</p>
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="text-vm-mutedFg hover:text-vm-fg" data-testid="chat-close" aria-label="Fechar chat">
                <X size={20} />
              </button>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-5 space-y-4" data-testid="chat-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[82%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                    m.role === "user" ? "bg-vm-gold text-vm-bg rounded-br-sm" : "bg-vm-muted text-vm-fg/90 rounded-bl-sm border border-vm-border"
                  }`}>
                    <MessageContent content={m.content} />
                  </div>
                </div>
              ))}
            </div>

            {messages.length <= 1 && (
              <div className="px-4 pb-2 flex flex-wrap gap-2">
                {SUGGESTIONS.map((s) => (
                  <button key={s} onClick={() => send(s)} className="text-xs px-3 py-1.5 rounded-full border border-vm-border text-vm-mutedFg hover:border-vm-gold/50 hover:text-vm-fg transition-colors" data-testid={`chat-suggestion`}>
                    {s}
                  </button>
                ))}
              </div>
            )}

            <div className="p-3 border-t border-vm-border flex items-center gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Escreva sua mensagem..."
                className="flex-1 bg-vm-muted border border-vm-border rounded-full px-4 py-2.5 text-sm outline-none focus:border-vm-gold/50 text-vm-fg placeholder:text-vm-mutedFg/60"
                data-testid="chat-input"
              />
              <button onClick={() => send()} disabled={busy} className="h-10 w-10 rounded-full bg-vm-gold flex items-center justify-center text-vm-bg disabled:opacity-50 shrink-0" data-testid="chat-send" aria-label="Enviar">
                <PaperPlaneTilt size={18} weight="fill" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="fixed bottom-6 right-4 sm:right-6 z-50 flex items-end gap-3">
        <AnimatePresence>
          {!open && (
            <motion.button
              key="callout"
              initial={{ opacity: 0, x: 20, scale: 0.8 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: 20, scale: 0.8 }}
              transition={{ delay: 2, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              onClick={() => { trackLeaseIntentClick(); setOpen(true); }}
              className="relative mb-2 group hidden sm:block"
              data-testid="chat-callout"
            >
              <div className="relative flex items-center gap-2.5 rounded-2xl bg-vm-bg/95 backdrop-blur-md border border-vm-gold/40 pl-4 pr-5 py-3 shadow-xl shadow-black/40 group-hover:border-vm-gold transition-colors">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-vm-gold opacity-70 animate-ping" />
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-vm-gold" />
                </span>
                <div className="text-left leading-tight">
                  <span className="block text-[10px] uppercase tracking-[0.2em] text-vm-mutedFg">Fale com a gente</span>
                  <span className="block font-serif text-base text-vm-gold">Aluguel de Lojas</span>
                </div>
                <span className="absolute -right-1.5 top-1/2 -translate-y-1/2 h-3 w-3 rotate-45 bg-vm-bg border-r border-b border-vm-gold/40 group-hover:border-vm-gold" />
              </div>
            </motion.button>
          )}
        </AnimatePresence>

        <motion.button
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 1.5, type: "spring", stiffness: 200 }}
          whileHover={{ scale: 1.06 }}
          whileTap={{ scale: 0.94 }}
          onClick={() => setOpen((v) => !v)}
          className="relative h-16 w-16 rounded-full flex items-center justify-center shrink-0 overflow-visible"
          data-testid="chat-toggle"
          aria-label="Fale conosco"
        >
          {!open && <span className="absolute inset-0 rounded-full bg-vm-gold/30 animate-ping" />}
          <span className="absolute inset-0 rounded-full bg-gradient-to-br from-vm-gold to-vm-goldSoft" />
          <span className="absolute inset-[3px] rounded-full bg-vm-bg overflow-hidden">
            <AnimatePresence mode="wait">
              {open ? (
                <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} className="absolute inset-0 flex items-center justify-center text-vm-gold">
                  <X size={26} weight="bold" />
                </motion.span>
              ) : (
                <motion.img
                  key="avatar"
                  src="/chat-avatar.png"
                  alt="Atendente virtual VM Piabetá"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="absolute inset-0 h-full w-full object-cover"
                  style={{ objectPosition: "52% 6%", transform: "scale(1.35)" }}
                />
              )}
            </AnimatePresence>
          </span>
        </motion.button>
      </div>
    </>
  );
}

function Dot({ d = 0 }) {
  return <motion.span className="h-1.5 w-1.5 rounded-full bg-vm-mutedFg inline-block" animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1, repeat: Infinity, delay: d }} />;
}

const WA_REGEX = /(https?:\/\/wa\.me\/\d+(?:\?[^\s)]*)?)/i;

function MessageContent({ content }) {
  if (!content) {
    return <span className="inline-flex gap-1"><Dot /><Dot d={0.15} /><Dot d={0.3} /></span>;
  }
  const match = content.match(WA_REGEX);
  const text = match ? content.replace(match[0], "").replace(/\(\s*\)/g, "").trim() : content;
  return (
    <>
      {text && <span className="whitespace-pre-line">{text}</span>}
      {match && (
        <a
          href={match[0]}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackLeaseIntentClick()}
          className="mt-2.5 inline-flex items-center gap-2 bg-[#25D366] text-white font-semibold text-xs px-4 py-2.5 rounded-full hover:brightness-105 transition-all shadow-sm"
          data-testid="chat-whatsapp-handoff"
        >
          <WhatsappLogo size={16} weight="fill" /> Falar no WhatsApp Oficial
        </a>
      )}
    </>
  );
}
